import React from 'react';
import { FiMail, FiPhone, FiArrowRight } from 'react-icons/fi';
import Button from '@components/common/Button';
import { CONTACT_INFO } from '@utils/constants';

/**
 * CTASection Component
 * Call-to-action section encouraging user engagement
 */
const CTASection = () => {
  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="relative rounded-3xl overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-bukawa-primary via-bukawa-primary-700 to-bukawa-primary-900">
            <div className="absolute inset-0 opacity-10">
              <div className="pattern-grid w-full h-full" />
            </div>
          </div>

          {/* Content */}
          <div className="relative z-10 py-16 md:py-20 lg:py-24 px-6 md:px-12">
            <div className="max-w-4xl mx-auto text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-bukawa-secondary text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-bukawa-secondary rounded-full animate-pulse" />
                Get Started Today
              </div>

              {/* Heading */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6 leading-tight">
                Ready to Partner with Quality?
              </h2>

              {/* Description */}
              <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
                Join over 1,000 satisfied customers who trust Bukawa for premium
                white maize flour. Contact us today for bulk orders or to learn
                about membership opportunities.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button
                  to="/contact"
                  variant="secondary"
                  size="lg"
                  icon={<FiArrowRight />}
                  iconPosition="right"
                >
                  Request a Quote
                </Button>
                <Button
                  to="/membership"
                  size="lg"
                  className="bg-white text-bukawa-primary hover:bg-white/90"
                >
                  Become a Member
                </Button>
              </div>

              {/* Contact Information */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-white/80">
                <a
                  href={`tel:${CONTACT_INFO.phone1}`}
                  className="flex items-center gap-2 hover:text-bukawa-secondary transition-colors"
                >
                  <FiPhone className="h-5 w-5" />
                  <span>{CONTACT_INFO.phone1}</span>
                </a>
                <span className="hidden sm:block w-px h-6 bg-white/30" />
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center gap-2 hover:text-bukawa-secondary transition-colors"
                >
                  <FiMail className="h-5 w-5" />
                  <span>{CONTACT_INFO.email}</span>
                </a>
              </div>
            </div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-bukawa-secondary rounded-full opacity-10 blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-bukawa-secondary rounded-full opacity-10 blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>
      </div>
    </section>
  );
};

/**
 * AlternativeCTA Component
 * Simpler CTA variant for other pages
 */
export const AlternativeCTA = ({
  title = "Have Questions?",
  description = "Our team is here to help you find the perfect solution for your needs.",
  primaryButton = { text: "Contact Us", link: "/contact" },
  secondaryButton = null,
}) => {
  return (
    <section className="section bg-bukawa-light">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-bukawa-primary mb-4">
            {title}
          </h2>
          <p className="text-lg text-bukawa-gray-700 mb-8">
            {description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              to={primaryButton.link}
              variant="primary"
              size="lg"
            >
              {primaryButton.text}
            </Button>
            {secondaryButton && (
              <Button
                to={secondaryButton.link}
                variant="outline"
                size="lg"
              >
                {secondaryButton.text}
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;